/**
 * PropertyAdvisorContext
 *
 * Central provider so any page can register the property it's showing and
 * any child (currently the RendPleX AI Advisor chat) can consume it without
 * prop-drilling.
 *
 * How to use:
 *   1. Wrap the page in <PropertyAdvisorProvider value={...}>
 *   2. In a child, call const ctx = useAdvisorContext();  // matches backend schema
 *
 * Design goals:
 *   - Consumers get a fully normalized object with backend field names.
 *   - Missing fields are stripped (undefined) so we never send stale zeros.
 *   - Adding a new field is a one-line change in `normalizeContext`.
 */
import React, { createContext, useContext, useMemo } from 'react';

const PropertyAdvisorContext = createContext(null);

/**
 * Strip undefined / null / empty-string fields so the AI never sees a zero
 * that just means "the user hasn't typed anything yet".
 */
const clean = (obj) => {
  const out = {};
  Object.entries(obj || {}).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    if (Number.isNaN(v)) return;
    out[k] = v;
  });
  return out;
};

const toNumber = (v) => {
  if (v === undefined || v === null || v === '') return undefined;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : undefined;
};

const toInt = (v) => {
  if (v === undefined || v === null || v === '') return undefined;
  const n = typeof v === 'number' ? v : parseInt(v, 10);
  return Number.isFinite(n) ? n : undefined;
};

/**
 * Turns a mixed raw + computed input into the exact shape our backend
 * `services.chat_schemas.PropertyContext` expects. Add new backend fields
 * by adding a line here — no other file needs to change.
 */
export function normalizeContext(raw = {}) {
  const purchase_price = toNumber(raw.purchase_price);
  const down_pct = toNumber(raw.down_payment_percent);
  const derivedDownAmt =
    purchase_price !== undefined && down_pct !== undefined
      ? Math.round((purchase_price * down_pct) / 100)
      : undefined;
  const derivedMortAmt =
    purchase_price !== undefined && derivedDownAmt !== undefined
      ? purchase_price - derivedDownAmt
      : undefined;

  const scenarios = raw.scenarios
    ? Object.fromEntries(
        Object.entries(raw.scenarios).map(([key, s]) => [
          key,
          clean({
            label: s?.label,
            monthly_cash_flow: toNumber(s?.monthly_cash_flow),
            cap_rate: toNumber(s?.cap_rate),
            cash_on_cash: toNumber(s?.cash_on_cash),
            dscr: toNumber(s?.dscr),
          }),
        ])
      )
    : undefined;

  const normalized = clean({
    address: raw.address,
    purchase_price,
    num_units: toInt(raw.num_units),
    annual_income: toNumber(raw.annual_income),
    monthly_income: toNumber(raw.monthly_income),
    down_payment_percent: down_pct,
    down_payment_amount: toNumber(raw.down_payment_amount) ?? derivedDownAmt,
    mortgage_amount: toNumber(raw.mortgage_amount) ?? derivedMortAmt,
    interest_rate: toNumber(raw.interest_rate),
    loan_term_years: toInt(raw.loan_term_years),
    annual_property_tax: toNumber(raw.annual_property_tax),
    annual_insurance: toNumber(raw.annual_insurance),
    monthly_maintenance: toNumber(raw.monthly_maintenance),
    vacancy_rate: toNumber(raw.vacancy_rate),
    property_management_percent: toNumber(raw.property_management_percent),
    monthly_mortgage: toNumber(raw.monthly_mortgage),
    monthly_expenses: toNumber(raw.monthly_expenses),
    monthly_cash_flow: toNumber(raw.monthly_cash_flow),
    annual_cash_flow: toNumber(raw.annual_cash_flow),
    noi: toNumber(raw.noi),
    cap_rate: toNumber(raw.cap_rate),
    cash_on_cash: toNumber(raw.cash_on_cash),
    roi: toNumber(raw.roi),
    deal_score: toInt(raw.deal_score),
    investment_rating: raw.investment_rating,
    suggested_offer_price: toNumber(raw.suggested_offer_price),
    risk_factors: Array.isArray(raw.risk_factors) && raw.risk_factors.length
      ? raw.risk_factors
      : undefined,
    scenarios,
    investor_profile: raw.investor_profile,
    deal_id: raw.deal_id,
  });
  // Portfolio-Aware Advisor metadata — passes through unchanged so any
  // consumer (currently AIPropertyChat) can read the current page's
  // contextType, header label, suggested questions and the compact
  // page_context dict destined for the backend.
  if (raw.__contextType) normalized.__contextType = raw.__contextType;
  if (raw.__contextLabel) normalized.__contextLabel = raw.__contextLabel;
  if (raw.__pageContext) normalized.__pageContext = raw.__pageContext;
  if (raw.__suggestedQuestions) normalized.__suggestedQuestions = raw.__suggestedQuestions;
  return normalized;
}

export function PropertyAdvisorProvider({ value, children }) {
  const memoValue = useMemo(() => normalizeContext(value), [value]);
  return (
    <PropertyAdvisorContext.Provider value={memoValue}>
      {children}
    </PropertyAdvisorContext.Provider>
  );
}

/**
 * Returns the normalized property snapshot from the nearest provider,
 * or null if no provider is mounted (so components can gracefully degrade).
 */
export function useAdvisorContext() {
  return useContext(PropertyAdvisorContext);
}
